import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import fundWorkshopImage from '../assets/harbor/fundwerkstatt.jpg';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { useGamification } from '../hooks/GamificationContext';
import { nonce as runtimeNonce } from '../utils/runtime';

const FUNDWORKSHOP_FALLBACK_RECIPES = [
  {
    slug: 'kronkorken_recyceln',
    title: 'Kronkorken recyceln',
    input_item: 'Alter Kronkorken',
    input_amount: 25,
    reward_label: 'Eisenbeschlag',
    reward_amount: 1,
    xp: 25,
    owned: 0,
    missing: 25,
    can_craft: false,
    note: 'Aus gesammeltem Metall wird wieder brauchbares Werftmaterial.',
  },
  {
    slug: 'naegel_einschmelzen',
    title: 'Nägel einschmelzen',
    input_item: 'Verrosteter Nagel',
    input_amount: 10,
    reward_label: 'Eisenbeschlag',
    reward_amount: 1,
    xp: 15,
    owned: 0,
    missing: 10,
    can_craft: false,
    note: 'Gefährlicher Schrott verschwindet vom Steg und stärkt dein Schiff.',
  },
  {
    slug: 'bananenschalen_verwerten',
    title: 'Bananenschalen verwerten',
    input_item: 'Bananenschale',
    input_amount: 10,
    reward_label: 'Harz',
    reward_amount: 1,
    xp: 15,
    owned: 0,
    missing: 10,
    can_craft: false,
    note: 'Organischer Beifang landet nicht im Meer, sondern im Werkstattkreislauf.',
  },
];

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (error) {
    return { message: text };
  }
}

export default function FundWorkshopPage() {
  const navigate = useNavigate();
  const sessionContext = useOptionalAppSession();
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const { applyGamificationPayload } = useGamification() || {};
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyRecipe, setBusyRecipe] = useState('');
  const [notice, setNotice] = useState(null);

  const activeRecipes = useMemo(
    () => (Array.isArray(recipes) && recipes.length > 0 ? recipes : FUNDWORKSHOP_FALLBACK_RECIPES),
    [recipes]
  );

  const loadState = useCallback(async ({ keepNotice = false } = {}) => {
    setLoading(true);
    if (!keepNotice) {
      setNotice(null);
    }

    try {
      const response = await fetch('/wp-json/pfandpirat/v1/fundwerkstatt', {
        credentials: 'include',
        headers: {
          'X-WP-Nonce': runtimeNonce,
        },
      });
      const data = await parseJsonResponse(response);

      if (!response.ok || !Array.isArray(data?.recipes)) {
        throw new Error(data?.message || 'Die Fundwerkstatt konnte nicht geladen werden.');
      }

      setRecipes(data.recipes);
    } catch (error) {
      if (!keepNotice) {
        setNotice({
          type: 'error',
          message: error?.message || 'Die Fundwerkstatt konnte nicht geladen werden.',
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const handleCraft = useCallback(async (recipe) => {
    if (!recipe?.slug || busyRecipe || loading || !recipe.can_craft) return;

    setBusyRecipe(recipe.slug);
    setNotice(null);

    try {
      const response = await fetch('/wp-json/pfandpirat/v1/fundwerkstatt/craft', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': runtimeNonce,
        },
        body: JSON.stringify({ recipe: recipe.slug }),
      });
      const data = await parseJsonResponse(response);

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Die Verwertung hat nicht geklappt.');
      }

      if (Array.isArray(data?.recipes)) {
        setRecipes(data.recipes);
      }

      if (data?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(data.session_patch);
      }

      if (Number(data?.awarded || 0) !== 0 && typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(data, {
          emitXpToast: true,
          event: data?.event || 'fundwerkstatt_recycle',
          awarded: Number(data?.awarded || 0),
          toastLabel: data?.xp_toast?.label || 'Fundwerkstatt',
        });
      }

      setNotice({
        type: 'success',
        message: data?.message || `${recipe.title} erfolgreich verwertet.`,
      });
    } catch (error) {
      setNotice({
        type: 'error',
        message: error?.message || 'Die Verwertung hat nicht geklappt.',
      });
      await loadState({ keepNotice: true });
    } finally {
      setBusyRecipe('');
    }
  }, [applyGamificationPayload, busyRecipe, loadState, loading, mergeSessionPatch]);

  const getButtonLabel = (recipe) => {
    if (busyRecipe === recipe.slug) return 'Wird verwertet...';
    if (loading) return 'Bestand laden...';
    if (recipe.can_craft) return 'Verwerten';
    const missing = Number(recipe.missing || 0);
    if (missing > 0) return `Noch ${missing} fehlen`;
    return 'Nicht verfügbar';
  };

  return (
    <div className="pfp-fundworkshop-page">
      <Helmet>
        <title>Fundwerkstatt - Pfandpirat</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pfp-fundworkshop-shell">
        <button type="button" className="pfp-harbor-back" onClick={() => navigate('/freibeuterhafen')}>
          ← Zurück zum Hafen
        </button>

        <section className="pfp-fundworkshop-hero" aria-label="Fundwerkstatt">
          <img src={fundWorkshopImage} alt="Fundwerkstatt am Hafen mit Sortierkisten, Werkbank und wiederverwerteten Fundstücken" />
          <div className="pfp-fundworkshop-hero-panel">
            <span className="pfp-harbor-kicker">Neu im Hafen</span>
            <h1>Fundwerkstatt</h1>
            <p>
              Häufige Fundstücke bekommen hier einen Zweck: verwerten statt wegwerfen,
              normale XP sammeln und nützliche Materialien für deine Reise gewinnen.
            </p>
          </div>
        </section>

        <section className="pfp-fundworkshop-recipes" aria-label="Verwertungen">
          <div className="pfp-fundworkshop-recipes-head">
            <span className="pfp-harbor-kicker">MVP-Rezepte</span>
            <h2>Erste Verwertungen</h2>
            <p>Wähle eine Verwertung, wenn Du genug Fundstücke gesammelt hast.</p>
          </div>

          {notice ? (
            <div className={`pfp-fundworkshop-notice is-${notice.type}`} role="status">
              {notice.message}
            </div>
          ) : null}

          <div className="pfp-fundworkshop-recipe-grid">
            {activeRecipes.map((recipe) => (
              <article
                key={recipe.slug || recipe.title}
                className={`pfp-fundworkshop-recipe-card ${recipe.can_craft ? 'is-ready' : 'is-missing'}`}
              >
                <strong>{recipe.title}</strong>
                <dl>
                  <div>
                    <dt>Einsatz</dt>
                    <dd>{Number(recipe.input_amount || 0)} {recipe.input_item}</dd>
                  </div>
                  <div>
                    <dt>Ergebnis</dt>
                    <dd>
                      {Number(recipe.reward_amount || 0)} {recipe.reward_label}
                      {' + '}
                      {Number(recipe.xp || 0)} XP
                    </dd>
                  </div>
                  <div>
                    <dt>Bestand</dt>
                    <dd className={recipe.can_craft ? 'is-enough' : 'is-short'}>
                      {Number(recipe.owned || 0)} / {Number(recipe.input_amount || 0)}
                    </dd>
                  </div>
                </dl>
                <p>{recipe.note}</p>
                <button
                  type="button"
                  disabled={loading || busyRecipe === recipe.slug || !recipe.can_craft}
                  onClick={() => handleCraft(recipe)}
                >
                  {getButtonLabel(recipe)}
                </button>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
